import {AfterViewInit, Component, Input, OnDestroy, ViewChild} from '@angular/core';
import {NgForm} from "@angular/forms";
import {MatPaginator, PageEvent} from "@angular/material/paginator";
import {ReplaySubject} from "rxjs";
import {UserPermissions} from "../../../../shared/interface/user-permissions";
import {MatSnackBar} from "@angular/material/snack-bar";
import {MatDialog} from "@angular/material/dialog";
import {AuthService} from "../../../../shared/services/auth.service";
import {ActivatedRoute, Router} from "@angular/router";
import {FilterService} from "../../../../shared/services/filter.service";
import {select, Store} from "@ngrx/store";
import {StoreRootState} from "../../../../shared/ngrx/reducers";
import {delay, distinctUntilChanged, takeUntil, tap} from "rxjs/operators";
import {CountedAndPaginated} from "../../../../shared/interface/response/counted-and-paginated";
import {getCurrentRouteState} from "../../../../shared/ngrx/selector";
import {RouterStateUrl} from "../../../../shared/ngrx/route-serializer";
import {Uuid} from "../../../../shared/interface/response/uuid";
import {JobResultDialogComponent} from "../../../../shared/components/job-result-dialog/job-result-dialog.component";
import {HttpErrorResponse} from "@angular/common/http";
import {InsuranceCompany} from "../../../../shared/interface/insurance-company";
import {CommissionUpdatePackageService} from "../../../../shared/services/commission-update-package.service";
import {CommissionUpdatePackage} from "../../../../shared/interface/commission-update-package";
import {MatSort, Sort} from "@angular/material/sort";
import {ConfirmDialogComponent} from "../../../../shared/components/confirm-dialog/confirm-dialog.component";

@Component({
  selector: 'app-commission-update-package-list',
  templateUrl: './commission-update-package-list.component.html',
  styleUrls: ['./commission-update-package-list.component.scss']
})
export class CommissionUpdatePackageListComponent implements OnDestroy, AfterViewInit {
  @ViewChild(MatSort) sort: MatSort;
  @ViewChild('filtersForm') filtersForm: NgForm;
  @ViewChild(MatPaginator) paginator: MatPaginator;
  @Input() enableFilters?: string[] = null;
  @Input() commissionUpdatePackageId?: string =  null;
  @Input() embeddedView = false;


  selectedInsuranceCompanies: InsuranceCompany[] = [];
  initialFilters: any;
  items: CommissionUpdatePackage[] = [];
  totalData = 0;
  displayedColumns: string[] = [
    'id', 'createAt', 'party', 'company', 'insuranceCompany', 'riskCode', 'internalRisk', 'active', 'createdBy', 'delete',
  ];
  userPermissions: ReplaySubject<UserPermissions>;
  processingRequest = false;
  sortBy = 'createdAt';
  sortOrder = 'desc';

  private destroyed$: ReplaySubject<boolean> = new ReplaySubject<boolean>(1);

  constructor(
    private commissionUpdatePackageService: CommissionUpdatePackageService,
    private snack: MatSnackBar,
    private dialog: MatDialog,
    private auth: AuthService,
    private activatedRoute: ActivatedRoute,
    private filterService: FilterService,
    private store: Store<StoreRootState>,
    private router: Router,
  ) {
    this.userPermissions = auth.getUserPermissions();
    this.commissionUpdatePackageService.CommissionUpdatePackageListSubject
      .pipe(
        takeUntil(this.destroyed$),
        tap((e: CountedAndPaginated<CommissionUpdatePackage>) => {
          this.items = e.items;
          this.totalData = e.pagination.total;
        })
      ).subscribe();
    this.parseFilters();

  }

  parseFilters(filters?: any): void {
    this.initialFilters = this.filterService.parseFiltersFromQueryParams({
      id: 'string',
      gte$createdAt: 'date',
      lte$createdAt: 'date',
      company: 'int',
      ofwca: 'string',
      partner: 'string',
      in$insuranceCompany: 'int[]',
      importer: 'int[]',
      contains$riskPack: 'string',
      contains$riskCode: 'string',
      in$risks: 'int[]',
      active: 'int',
    }, filters);
  }

  ngOnDestroy(): void {
    this.destroyed$.next(true);
    this.destroyed$.complete();
  }

  ngAfterViewInit(): void {
    this.paginator.page
      .pipe(
        takeUntil(this.destroyed$)
      )
      .subscribe((event: PageEvent) => {
        if (this.embeddedView) {
          this.loadListData();
        } else {
        this.router.navigate([this.activatedRoute.snapshot.routeConfig.path, this.filterService.parseFiltersToQueryParams({
          page: event.pageIndex,
          pageSize: event.pageSize,
          sortBy: this.sortBy,
          sortOrder: this.sortOrder,
          ...this.filtersForm.value
        })]);
          }
        });
    this.sort.sortChange
      .pipe(takeUntil(this.destroyed$))
      .subscribe((event: Sort) => {
        this.sortChanged(event);
      });

    if (!this.embeddedView) {
    this.store
      .pipe(
        select(getCurrentRouteState),
        delay(0),
        distinctUntilChanged((prev: RouterStateUrl, curr: RouterStateUrl) => prev.url === curr.url),
        takeUntil(this.destroyed$)
      )
      .subscribe((state: RouterStateUrl) => {
        this.parseFilters(state.params);
        this.paginator.pageIndex = state.params.page;
        this.paginator.pageSize = state.params.pageSize;
        this.sortBy = state.params?.sortBy ? state.params.sortBy : 'createAt';
        this.sortOrder = state.params?.sortOrder ? state.params.sortOrder : 'desc';
        this.loadListData();
      });
    } else {
      this.loadListData();
    }
  }

  sortChanged(event: Sort): void {
    this.sortBy = event.active;
    this.sortOrder = event.direction.toUpperCase();
    this.loadListData();
  }

  filter(): void {
    if (this.embeddedView) {
      if (this.paginator.pageIndex === 0) {
        this.loadListData();
      } else {
        this.paginator.firstPage();
      }
    } else {
      this.router.navigate([this.activatedRoute.snapshot.routeConfig.path, this.filterService.parseFiltersToQueryParams({
        page: 0,
        pageSize: this.paginator.pageSize,
        sortBy: this.sortBy,
        sortOrder: this.sortOrder,
        ...this.filtersForm.value,
      })]);
    }
  }

  loadListData(): void {
    this.commissionUpdatePackageService.loadList(
      this.paginator.pageIndex,
      this.paginator.pageSize,
      this.initialFilters,
      {
        sortBy: this.sortBy,
        sortOrder: this.sortOrder
      }
    );

  }

  downloadReport(): void {
    this.commissionUpdatePackageService.downloadReport(this.initialFilters)
      .subscribe((response: Uuid) => {
        this.dialog.open(JobResultDialogComponent, {
          disableClose: true,
          width: '500px',
          data: {jobId: response.uuid, showProgress: false},
        });
      }, (error: HttpErrorResponse) => {
        this.snack.open('Przy generowaniu raportu wystąpił błąd', error.status.toString(), {duration: 2000});
      });
  }

  resetFilters(): void {
    this.filtersForm.resetForm();
    if (this.embeddedView) {
      this.loadListData();
    } else {
      this.router.navigate([this.activatedRoute.snapshot.routeConfig.path, {
        page: 0,
        pageSize: this.paginator.pageSize,
        sortBy: this.sortBy,
        sortOrder: this.sortOrder,
      }]);
    }
  }
  onImgError($event: ErrorEvent): void {
    // @ts-ignore
    $event.target.src = '/assets/avatar.png';
  }
  addConfig(): void {

  }

  delete(uuid: string): void {
    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      width: '500px',
      data: { text: 'Czy na pewno chcesz usunąć konfigurację?' },
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        this.commissionUpdatePackageService.delete(uuid)
          .subscribe({
            next: () => {
              this.snack.open('Pomyślnie usunięto konfigurację', '', { duration: 2000 });
              this.loadListData();
            },
            error: (err) => {
              const errorText = err?.error.error?.text || 'Wystąpił nieznany błąd podczas usuwania.';
              this.snack.open(errorText, '', { duration: 4000 });
            }
          });
      }
    });
  }

}
